/**
 * RegistrationHub - Unified Registration Orchestrator
 * 
 * Routes:
 *   /registration           → New registration (course/cert selection)
 *   /registration/:id       → Resume/view existing registration
 * 
 * This component determines which sub-component to render based on:
 *   1. Registration type (standalone course vs full certification)
 *   2. Registration stage (draft, pending, payment, enrolled, etc.)
 *   3. Policy requirements (email verification, approval, etc.)
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from './utils/supabaseClient';
import {
    Box,
    Center,
    Spinner,
    Text,
    VStack,
    HStack,
    Alert,
    AlertIcon,
    AlertTitle,
    AlertDescription,
    Button,
    Icon,
    Container,
    Heading,
    Card,
    CardBody,
    SimpleGrid,
    Flex,
} from '@chakra-ui/react';
import { CheckCircleIcon, InfoIcon } from '@chakra-ui/icons';
import { motion } from 'framer-motion';
import { HiBookOpen, HiAcademicCap } from 'react-icons/hi';
import { COLORS } from './styles/colors';
import {
    advancePublicRegistrationJourney,
    fetchPublicRegistration,
} from './utils/registrationPortalApi';

// Import existing components for rendering
import { Registration } from './Registration';
import FullCertRegistration from './FullCertRegistration';
import { RegistrationPending } from './RegistrationPending';
import { CourseInterestReceived } from './CourseInterestReceived';
import { RegistrationFeePayment } from './RegistrationFeePayment';
import { Enrollment } from './Enrollment';
import { StudentPackage } from './StudentPackage';

// Loading component
const LoadingState = ({ message = 'Loading registration...' }) => (
    <Center minH="60vh">
        <VStack spacing={4}>
            <Spinner size="xl" color="blue.500" thickness="4px" />
            <Text color="gray.600">{message}</Text>
        </VStack>
    </Center>
);

// Error component
const ErrorState = ({ title, message, onRetry }) => (
    <Center minH="60vh">
        <Alert
            status="error"
            variant="subtle"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            borderRadius="lg"
            maxW="md"
            p={8}
        >
            <AlertIcon boxSize="40px" mr={0} />
            <AlertTitle mt={4} mb={1} fontSize="lg">
                {title}
            </AlertTitle>
            <AlertDescription maxWidth="sm">
                {message}
            </AlertDescription>
            {onRetry && (
                <Button mt={4} colorScheme="blue" onClick={onRetry}>
                    Try Again
                </Button>
            )}
        </Alert>
    </Center>
);

const REGISTRATION_TYPE_OPTIONS = [
    {
        id: 'course',
        title: 'Course',
        description: 'Enroll in a single course or short program with a scheduled start date.',
        icon: HiBookOpen,
        path: '/register?type=course',
    },
    {
        id: 'certification',
        title: 'Certification Program',
        description: 'Apply for a full diploma or certification pathway spanning multiple modules.',
        icon: HiAcademicCap,
        path: '/register?type=certification',
    },
];

// Course/Certification selection component (for new registrations)
const CourseSelection = () => {
    const navigate = useNavigate();

    return (
        <Box
            minH={{ base: 'calc(100vh - 72px)', md: 'calc(100vh - 80px)' }}
            bg="white"
            py={{ base: 6, md: 12 }}
            px={{ base: 4, sm: 6 }}
        >
            <Container maxW="4xl">
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.45 }}
                >
                    <VStack spacing={{ base: 6, md: 10 }} align="stretch">
                        <Box textAlign="center" px={{ base: 1, md: 0 }}>
                            <Heading
                                as="h1"
                                size={{ base: 'lg', md: 'xl' }}
                                color={COLORS.maastrichtBlue}
                                fontWeight="700"
                                mb={2}
                            >
                                Start Your Registration
                            </Heading>
                            <Text
                                color={COLORS.shadowBlue}
                                fontSize={{ base: 'sm', md: 'md' }}
                                maxW="md"
                                mx="auto"
                            >
                                Choose the type of program you want to register for
                            </Text>
                        </Box>

                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 4, md: 6 }}>
                            {REGISTRATION_TYPE_OPTIONS.map((option, index) => (
                                <motion.div
                                    key={option.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.12 * index + 0.15, duration: 0.45 }}
                                    style={{ height: '100%' }}
                                >
                                    <Card
                                        bg="white"
                                        borderRadius="xl"
                                        boxShadow="md"
                                        border="1px solid"
                                        borderColor="gray.200"
                                        overflow="hidden"
                                        h="full"
                                        cursor="pointer"
                                        transition="all 0.25s ease"
                                        _hover={{
                                            boxShadow: 'lg',
                                            transform: 'translateY(-4px)',
                                            borderColor: 'gray.300',
                                        }}
                                        _active={{ transform: 'translateY(-2px)' }}
                                        onClick={() => navigate(option.path)}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={(event) => {
                                            if (event.key === 'Enter' || event.key === ' ') {
                                                event.preventDefault();
                                                navigate(option.path);
                                            }
                                        }}
                                    >
                                        <CardBody p={{ base: 5, md: 6 }}>
                                            <HStack spacing={4} align="flex-start" w="full">
                                                <Flex
                                                    align="center"
                                                    justify="center"
                                                    flexShrink={0}
                                                    w={{ base: 12, md: 14 }}
                                                    h={{ base: 12, md: 14 }}
                                                    borderRadius="xl"
                                                    bg="gray.50"
                                                >
                                                    <Icon
                                                        as={option.icon}
                                                        boxSize={{ base: 6, md: 7 }}
                                                        color="gray.800"
                                                    />
                                                </Flex>
                                                <VStack align="start" spacing={1.5} flex={1}>
                                                    <Heading
                                                        as="h2"
                                                        size={{ base: 'sm', md: 'md' }}
                                                        color="gray.800"
                                                    >
                                                        {option.title}
                                                    </Heading>
                                                    <Text
                                                        color="gray.600"
                                                        fontSize={{ base: 'sm', md: 'md' }}
                                                        lineHeight="tall"
                                                    >
                                                        {option.description}
                                                    </Text>
                                                </VStack>
                                            </HStack>
                                        </CardBody>
                                    </Card>
                                </motion.div>
                            ))}
                        </SimpleGrid>
                    </VStack>
                </motion.div>
            </Container>
        </Box>
    );
};

/**
 * DraftResume - Shown when a user navigates to /register/:id for a draft registration.
 * This happens when a user clicks "Continue Registration" from the VerifyEmail page
 * instead of returning to their original form tab.
 * 
 * The draft registration was created to hold the email verification token,
 * but the full form data lives in the React state of the original tab.
 */
const DraftResume = ({ registration }) => {
    const isEmailVerified = registration?.email_verified;
    const participantEmail = registration?.participant_email;

    return (
        <Center minH="60vh">
            <VStack
                spacing={6}
                p={8}
                bg="white"
                borderRadius="lg"
                shadow="md"
                maxW="lg"
                w="full"
            >
                {isEmailVerified ? (
                    <>
                        <Box bg="green.100" borderRadius="full" p={4}>
                            <CheckCircleIcon boxSize={10} color="green.500" />
                        </Box>
                        <VStack spacing={2}>
                            <Text fontSize="xl" fontWeight="bold" color="gray.800">
                                Email Verified!
                            </Text>
                            <Text color="gray.600" textAlign="center">
                                Your email ({participantEmail}) has been verified successfully.
                            </Text>
                        </VStack>
                        <Alert status="info" borderRadius="md">
                            <AlertIcon />
                            <Text fontSize="sm">
                                If your registration form is still open in another tab,
                                please return there to complete your submission.
                                The form will automatically detect your verification.
                            </Text>
                        </Alert>
                    </>
                ) : (
                    <>
                        <Box bg="yellow.100" borderRadius="full" p={4}>
                            <InfoIcon boxSize={10} color="yellow.600" />
                        </Box>
                        <VStack spacing={2}>
                            <Text fontSize="xl" fontWeight="bold" color="gray.800">
                                Registration In Progress
                            </Text>
                            <Text color="gray.600" textAlign="center">
                                This registration is still being filled out.
                            </Text>
                        </VStack>
                        <Alert status="warning" borderRadius="md">
                            <AlertIcon />
                            <Text fontSize="sm">
                                If your registration form is still open in another tab,
                                please return there to continue. Otherwise, you can
                                start a new registration below.
                            </Text>
                        </Alert>
                    </>
                )}

                <Button
                    colorScheme="blue"
                    size="lg"
                    onClick={() => window.close()}
                    w="full"
                >
                    Go back to registration form
                </Button>
            </VStack>
        </Center>
    );
};

/**
 * Determines the next step after approval based on the policy snapshot.
 * Similar to getNextRegistrationStage but skips the approval check
 * (since we're already past approval).
 * 
 * @param {Object} policySnapshot - The policy_snapshot stored on the registration
 * @returns {string} The view type to render
 */
const getPostApprovalViewType = (policySnapshot) => {
    if (!policySnapshot) return 'enrollment';

    // After approval, check what's next:
    // 1. Documents required → enrollment (documents + payment)
    if (policySnapshot.requires_documents) {
        return 'enrollment';
    }

    // 2. Payment required → enrollment (handles payment)
    const commitmentType = policySnapshot.commitment_type || 'full';
    if (commitmentType !== 'none' && commitmentType !== 'interest_registration') {
        return 'enrollment';
    }

    // 3. No documents or payment needed → enrolled
    return 'student_package';
};

/**
 * Determines which view to render based on registration data
 * 
 * View types:
 * - 'selection': Choose course or certification
 * - 'course_form': Fill out course registration form (new, no ID)
 * - 'cert_form': Fill out certification registration form (new, no ID)
 * - 'draft_resume': Draft registration accessed by ID (e.g., after email verification)
 * - 'enrollment': Documents, signature, and payment (Enrollment.js)
 * - 'pending': Status/waiting view (RegistrationPending.js)
 */
const getViewType = (registration) => {
    if (!registration) return 'selection';
    
    const stage = registration.registration_stage;
    // Cert registrations store the run on draft_cert_run_code (not full_certification_id / certification_run_id)
    const isCertification = !!registration.draft_cert_run_code;
    
    // Map stages to views
    switch (stage) {
        case 'draft':
            // Draft registrations are placeholders created for email verification.
            // The full form data lives in the React state of the original tab.
            // Show a helpful message instead of a blank form.
            return 'draft_resume';
        
        case 'documents_pending':
            // Documents/signature required — always show enrollment view.
            // Enrollment handles both documents and payment; if payment_balance is 0
            // (e.g. interest_registration), it will only show documents + signature.
            return 'enrollment';
        
        case 'interest_received':
            // Course run not confirmed yet - show interest received page
            return 'interest_received';
        
        case 'registration_fee_pending':
            // Registration fee payment required before approval (step 4 in fixed sequence)
            return 'registration_fee_payment';
        
        case 'pending':
        case 'pending_approval':
            // Awaiting admin approval
            return 'pending';
        
        case 'payment_pending':
        case 'payment_in_progress':
            // Awaiting payment.
            // If the policy snapshot says free (interest_registration), treat as enrolled.
            if (registration.policy_snapshot?.commitment_type === 'interest_registration') {
                return 'student_package';
            }
            return 'enrollment';
        
        case 'amended':
            // Registration resubmitted after rejection - awaiting review
            return 'pending';
        
        case 'approved':
            // Admin has approved - determine what comes next based on policy
            // Instead of showing the pending view, route to the appropriate next step
            return getPostApprovalViewType(registration.policy_snapshot);
        
        case 'enrolled':
        case 'completed':
        case 'payment_received':
            // Already enrolled - render student package inline
            return 'student_package';
        
        case 'rejected':
            // Rejected registrations re-render the registration form
            // with pre-filled data and rejection reason so the user can amend and resubmit
            if (isCertification) {
                // Certification registration → FullCertRegistration (handles its own data loading via :id param)
                return 'cert_form_edit';
            }
            // Standalone course registration → Registration with existingRegistration prop
            return 'course_form_edit';
        
        case 'cancelled':
            // Registration ended
            return 'pending';
        
        default:
            // Unknown stage - show pending view which handles various states
            console.log(`Unknown registration stage: ${stage}, defaulting to pending view`);
            return 'pending';
    }
};

const RegistrationHub = () => {
    const { id: registrationID } = useParams();
    const registrationId = registrationID; // Normalize naming
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [registration, setRegistration] = useState(null);
    const [viewType, setViewType] = useState('selection');

    // When user lands on /register/:id?token=xxx (e.g. from verify-email link), run verification then clean URL
    const searchToken = React.useMemo(() => new URLSearchParams(location.search).get('token'), [location.search]);
    useEffect(() => {
        if (!registrationId || !searchToken) return;
        let cancelled = false;
        (async () => {
            try {
                const { data, error: invokeError } = await supabase.functions.invoke('verify-email', {
                    body: { token: searchToken },
                });
                if (cancelled) return;
                if (!invokeError && data?.success) {
                    // Remove token from URL and refetch registration so email_verified is updated
                    navigate(`/register/${registrationId}`, { replace: true });
                    const { data: updated } = await fetchPublicRegistration(supabase, registrationId);
                    if (updated) {
                        setRegistration(updated);
                        setViewType(getViewType(updated));
                    }
                }
            } catch (e) {
                console.warn('Verify-email from register link:', e);
            }
        })();
        return () => { cancelled = true; };
    }, [registrationId, searchToken, navigate]);

    // Fetch registration data if ID is provided
    useEffect(() => {
        const fetchRegistration = async () => {
            if (!registrationId) {
                // No ID - show course/cert selection
                setViewType('selection');
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError(null);

                let { data, error: fetchError } = await fetchPublicRegistration(
                    supabase,
                    registrationId,
                );

                if (fetchError) {
                    if (fetchError.code === 'PGRST116') {
                        setError({
                            title: 'Registration Not Found',
                            message: `No registration found with ID: ${registrationId}`,
                        });
                    } else {
                        throw fetchError;
                    }
                    return;
                }

                // Auto-advance from 'approved': admin only needs to set 'approved';
                // we move to the correct next stage (documents_pending / payment_pending)
                // so the stage column always reflects the true current step.
                if (data.registration_stage === 'approved') {
                    const snap = data.policy_snapshot ?? {};
                    const ct = snap.commitment_type || 'full';
                    let nextStage;
                    if (snap.requires_documents) {
                        nextStage = 'documents_pending';
                    } else if (ct !== 'interest_registration' && ct !== 'none') {
                        nextStage = 'payment_pending';
                    } else {
                        nextStage = 'enrolled';
                    }
                    console.log(`[RegistrationHub] Requesting server advance from approved → ${nextStage}`);
                    const advanceResult = await advancePublicRegistrationJourney(supabase, {
                        registrationId,
                        expectedUpdatedAt: data.portal_updated_at,
                    });
                    if (advanceResult.error) throw advanceResult.error;
                    if (advanceResult.data) data = advanceResult.data;
                }

                setRegistration(data);
                setViewType(getViewType(data));
            } catch (err) {
                console.error('Error fetching registration:', err);
                setError({
                    title: 'Error Loading Registration',
                    message: err.message || 'An unexpected error occurred.',
                });
            } finally {
                setLoading(false);
            }
        };

        fetchRegistration();
    }, [registrationId, location.state]); // location.state triggers re-fetch after amendment navigation

    // Handle URL query params for new registrations (re-run when search changes)
    useEffect(() => {
        if (registrationId) return; // Only for new registrations

        const params = new URLSearchParams(location.search);
        const type = params.get('type');

        if (type === 'course') {
            setViewType('course_form');
            setLoading(false);
        } else if (type === 'certification') {
            setViewType('cert_form');
            setLoading(false);
        } else {
            setViewType('selection');
            setLoading(false);
        }
    }, [registrationId, location.search]);

    // Loading state
    if (loading) {
        return <LoadingState />;
    }

    // Error state
    if (error) {
        return (
            <ErrorState
                title={error.title}
                message={error.message}
                onRetry={() => window.location.reload()}
            />
        );
    }

    // Render based on view type
    switch (viewType) {
        case 'selection':
            return <CourseSelection />;

        case 'course_form':
            // Render standalone course registration form
            // Pass registration data if resuming
            return <Registration />;

        case 'cert_form':
            // Render full certification registration form
            // The component handles its own ID from URL or creates new
            return <FullCertRegistration />;

        case 'draft_resume':
            // Draft registration accessed by URL (e.g., after email verification)
            // Show helpful message instead of blank form
            return <DraftResume registration={registration} />;

        case 'course_form_edit':
            // Render standalone course registration form with existing data pre-filled
            // Used when a rejected registration is being edited/amended
            return <Registration existingRegistration={registration} />;

        case 'cert_form_edit':
            // Render full certification registration form with existing data pre-filled
            // FullCertRegistration loads data itself from :id param
            return <FullCertRegistration />;

        case 'student_package':
            // Already enrolled - render student package under /register/:id
            // StudentPackage uses useParams() for registrationID which matches our route param
            return <StudentPackage />;

        case 'registration_fee_payment':
            // Registration fee required before approval (step 4 in fixed sequence)
            return <RegistrationFeePayment registrationId={registrationId} />;

        case 'enrollment':
            // Render documents, signature, and payment view
            // Enrollment uses registrationID from useParams
            return <Enrollment />;

        case 'interest_received':
            // Course run not yet confirmed – show interest received page
            return <CourseInterestReceived registrationId={registrationId} />;

        case 'pending':
            // Render pending/status view
            return <RegistrationPendingWrapper registrationId={registrationId} />;

        default:
            return (
                <ErrorState
                    title="Unknown State"
                    message={`Unknown view type: ${viewType}`}
                />
            );
    }
};

/**
 * Wrapper for RegistrationPending to handle the registrationID
 * Since RegistrationPending uses useParams, we need to ensure the ID is in the URL
 */
const RegistrationPendingWrapper = ({ registrationId }) => {
    return <RegistrationPending registrationId={registrationId} />;
};

export default RegistrationHub;
